/**
 * 客服工作台 Vuex 状态管理模块
 */
import { request } from '../../../public/js/request'
import agentDefaultAvatar from '@/assets/images/agent.png'
import customerDefaultAvatar from '@/assets/images/customer.jpg'

// 音频上下文单例
let audioContext = null;
// 标题闪烁定时器
let titleFlashTimer = null;
let originalTitle = document.title;

export default {
  namespaced: true,

  state: {
    // 会话列表 (当前视图)
    conversations: [],
    // 当前会话的消息列表
    messages: [],
    messagesCache: {}, // 消息缓存 { conversationId: messages }
    // 客服信息
    agentInfo: null,
    // WebSocket 连接状态
    wsConnected: false,
    // 会话筛选状态
    conversationFilter: 'all', // all, open, resolved
    // 音频是否已解锁
    audioUnlocked: false,
    // 当前活动会话 (原始对象)
    activeConversation: null,

    // [NEW] 缓存所有渠道的会话列表 { [inboxId]: [conv1, conv2] }
    // 键改为 inboxId (Number/String)
    conversationsMap: {},
    // [NEW] 客服渠道列表
    inboxList: [],
    // [NEW] 当前选中的渠道ID
    currentInboxId: null,
    // [NEW] 客户信息缓存 { "inboxType_buyerId": infoObj }
    customerInfoCache: {},
    // [NEW] 最近订单缓存 { "inboxType_buyerId": [orders] }
    recentOrdersCache: {}
  },

  mutations: {
    // 设置会话列表 (直接替换当前视图)
    SET_CONVERSATIONS(state, conversations) {
      state.conversations = mapConversations(conversations)
    },

    // [NEW] 设置指定渠道的会话列表 (存入缓存)
    SET_CONVERSATIONS_FOR_INBOX(state, { inboxId, list }) {
      const mappedList = mapConversations(list)
      state.conversationsMap = {
        ...state.conversationsMap,
        [inboxId]: mappedList
      }

      // 如果是当前选中的渠道，也更新视图
      if (state.currentInboxId === inboxId) {
        state.conversations = mappedList
      }
    },

    // [NEW] 从缓存加载会话到视图
    SET_CONVERSATIONS_FROM_CACHE(state, inboxId) {
      if (state.conversationsMap[inboxId]) {
        state.conversations = state.conversationsMap[inboxId]
      } else {
        state.conversations = []
      }
    },

    // 设置当前活动会话
    SET_ACTIVE_CONVERSATION(state, conversation) {
      state.activeConversation = conversation
    },

    // [NEW] 设置渠道列表
    SET_INBOX_LIST(state, list) {
      state.inboxList = list
      // 初始化 map
      list.forEach(inbox => {
        if (!state.conversationsMap[inbox.inboxId]) {
          state.conversationsMap[inbox.inboxId] = []
        }
      })
    },

    // [NEW] 设置当前渠道ID
    SET_CURRENT_INBOX_ID(state, inboxId) {
      state.currentInboxId = inboxId
    },

    // [NEW] 设置客户信息缓存
    SET_CUSTOMER_INFO_CACHE(state, { key, info }) {
      state.customerInfoCache = {
        ...state.customerInfoCache,
        [key]: info
      }
    },

    // [NEW] 设置最近订单缓存
    SET_RECENT_ORDERS_CACHE(state, { key, orders }) {
      state.recentOrdersCache = {
        ...state.recentOrdersCache,
        [key]: orders
      }
    },

    // 从会话列表中移除会话
    REMOVE_CONVERSATION(state, conversationId) {
      // 1. 从当前视图移除
      state.conversations = state.conversations.filter(conv => conv.id !== conversationId)

      // 2. 从缓存中移除
      const inboxId = state.currentInboxId
      if (inboxId && state.conversationsMap[inboxId]) {
        const newList = state.conversationsMap[inboxId].filter(conv => conv.id !== conversationId)
        state.conversationsMap = { ...state.conversationsMap, [inboxId]: newList }
      }

      // 3. 检查活动会话
      if (state.activeConversation && state.activeConversation.id === conversationId) {
        state.activeConversation = null
      }
      // 4. 清除消息缓存
      delete state.messagesCache[conversationId]
    },

    // 设置消息列表
    SET_MESSAGES(state, { messages, conversationId }) {
      // 防止竞态条件：如果当前活动会话ID不匹配，则不更新视图
      if (!state.activeConversation || state.activeConversation.id !== conversationId) {
        // 但仍然可以更新缓存 (如果需要的话)
        // 这里选择只更新缓存，不更新 stae.messages
        const mapped = messages.map(msg => mapMessage(msg))
        state.messagesCache[conversationId] = mapped
        return
      }

      // 映射 Chatwoot 数据结构到前端格式
      const mappedMessages = messages.map(msg => mapMessage(msg))
      state.messages = mappedMessages

      // 更新消息缓存
      state.messagesCache[conversationId] = state.messages

      // 尝试更新会话列表中的预览信息
      updateConversationPreview(state, conversationId, mappedMessages)
    },

    // 头部添加消息 (加载历史消息)
    PREPEND_MESSAGES(state, messages) {
      const mappedMessages = messages.map(msg => mapMessage(msg))
      // 过滤重复消息
      const newMessages = mappedMessages.filter(newMsg =>
        !state.messages.some(existingMsg => existingMsg.id === newMsg.id)
      )
      state.messages = [...newMessages, ...state.messages]

      // 更新缓存
      if (state.activeConversation) {
        state.messagesCache[state.activeConversation.id] = state.messages
      }
    },

    // 添加消息 (新消息)
    ADD_MESSAGE(state, message) {
      // 检查消息是否已存在
      const exists = state.messages.some(m => m.id === message.id)
      if (exists) {
        return
      }

      const mappedMessage = mapMessage(message)
      state.messages = [...state.messages, mappedMessage]

      // 更新缓存
      if (state.activeConversation) {
        state.messagesCache[state.activeConversation.id] = state.messages
        // 更新预览
        updateConversationPreview(state, state.activeConversation.id, state.messages)
      }
    },

    // 根据临时ID更新消息 (用于乐观更新)
    UPDATE_MESSAGE_BY_TEMP_ID(state, { tempId, message }) {
      const index = state.messages.findIndex(m => m.tempId === tempId)
      if (index !== -1) {
        const mappedMessage = mapMessage(message)
        mappedMessage.status = 'sent' // 发送成功

        // 使用 splice 确保响应式更新
        state.messages.splice(index, 1, mappedMessage)

        // 更新缓存
        if (state.activeConversation) {
          state.messagesCache[state.activeConversation.id] = state.messages
          updateConversationPreview(state, state.activeConversation.id, state.messages)
        }
      }
    },

    // 更新会话
    UPDATE_CONVERSATION(state, conversation) {
      console.log('更新会话',state)
      console.log('更新会话conversation',conversation)
      // 1. 确定目标 Inbox ID
      let targetId = state.currentInboxId

      // 优先使用传入的 inbox_id
      if (conversation.inbox_id || conversation._raw?.inbox_id) {
        targetId = conversation.inbox_id || conversation._raw?.inbox_id
      } else {
        // 如果没有 inbox_id，尝试全量遍历 Map 查找该 conversation 属于哪个 list
        // 注意：这里的 key 现在是 inboxId
        for (const id in state.conversationsMap) {
          const found = state.conversationsMap[id].find(c => c.id === conversation.id)
          if (found) {
            targetId = Number(id) // key 是 string，转换为 number 对比
            break
          }
        }
      }

      // 确保 Map 已初始化
      if (!state.conversationsMap[targetId]) {
        state.conversationsMap = { ...state.conversationsMap, [targetId]: [] }
      }

      const list = state.conversationsMap[targetId] || []
      const index = list.findIndex(c => c.id === conversation.id)

      let newList = [...list]
      if (index !== -1) {
        // 更新现有
        const existing = list[index]
        const updatedConv = { ...existing, ...conversation }
        // 确保头像不被空值覆盖
        if (!updatedConv.contactAvatar) {
          updatedConv.contactAvatar = customerDefaultAvatar
        }
        newList.splice(index, 1, updatedConv)
      } else {
        // 新增 (添加到顶部)
        const newConv = { ...conversation }
        if (!newConv.contactAvatar) {
          newConv.contactAvatar = customerDefaultAvatar
        }
        newList.unshift(newConv)
        // 清除旧缓存
        if (state.messagesCache[conversation.id]) {
          delete state.messagesCache[conversation.id]
        }
      }

      // 更新 Map
      state.conversationsMap = { ...state.conversationsMap, [targetId]: newList }

      // 如果是当前 inbox，同步更新视图
      if (targetId === state.currentInboxId) {
        state.conversations = newList
      }
    },

    // [NEW] 标记指定会话已读 (更新 Map - key 为 inboxId)
    MARK_CONVERSATION_READ_IN_MAP(state, { id, inboxId }) {
      // 如果未提供 inboxId，尝试遍历查找
      let targetId = inboxId
      if (!targetId) {
        for (const key in state.conversationsMap) {
          if (state.conversationsMap[key].some(c => c.id === id)) {
            targetId = Number(key)
            break
          }
        }
      }

      if (targetId && state.conversationsMap[targetId]) {
        const list = state.conversationsMap[targetId]
        const conv = list.find(c => c.id === id)
        if (conv) {
          const newConv = { ...conv, unreadCount: 0 }
          const index = list.indexOf(conv)
          const newList = [...list] // 浅拷贝数组
          newList.splice(index, 1, newConv)

          // 更新 Map
          state.conversationsMap = { ...state.conversationsMap, [targetId]: newList }

          // 如果是当前 inbox，也更新视图
          if (targetId === state.currentInboxId) {
            state.conversations = newList
          }
        }
      }
    },

    // 标记会话为已读 (兼容旧代码，基于当前视图)
    MARK_CONVERSATION_READ(state, conversationId) {
      // 复用新逻辑
      this.commit('agent/MARK_CONVERSATION_READ_IN_MAP', { id: conversationId, inboxId: state.currentInboxId })
    },

    // 设置客服信息
    SET_AGENT_INFO(state, info) {
      state.agentInfo = info
    },

    // 设置 WebSocket 连接状态
    SET_WS_CONNECTED(state, connected) {
      state.wsConnected = connected
    },

    // 设置会话筛选状态
    SET_CONVERSATION_FILTER(state, filter) {
      state.conversationFilter = filter
    },

    // 清除指定会话的消息缓存
    CLEAR_MESSAGE_CACHE(state, conversationId) {
      if (conversationId) {
        delete state.messagesCache[conversationId]
      } else {
        state.messagesCache = {}
      }
    },

    // 重置状态
    RESET_STATE(state) {
      state.conversations = []
      state.activeConversation = null
      state.messages = []
      state.messagesCache = {}
      state.agentInfo = null
      state.wsConnected = false
      state.conversationFilter = 'all'
      state.audioUnlocked = false
      // 注意：不重置 inboxList 和 conversationsMap，以便保留缓存
    },

    // 设置音频解锁状态
    SET_AUDIO_UNLOCKED(state, unlocked) {
      state.audioUnlocked = unlocked
    }
  },

  actions: {
    // 选择会话
    selectConversation({ commit, dispatch }, conversation) {
      if (!conversation) {
        commit('SET_ACTIVE_CONVERSATION', null)
        return
      }

      commit('SET_ACTIVE_CONVERSATION', conversation)

      // 加载消息
      dispatch('loadMessages', { conversationId: conversation.id })

      // 延迟标记已读
      setTimeout(() => {
        dispatch('markAsRead', conversation.id)
      }, 300)
    },

    // 加载会话列表 (支持按 inboxId 加载)
    async loadConversations({ commit, state }, params = {}) {
      try {
        // 优先使用参数中的 inboxId，否则使用当前选中的 ID
        const inboxId = params.inboxId || state.currentInboxId
        if (!inboxId) return

        // [适配] 后端可能仍需要 inboxType，我们通过 inboxId 查找它
        let inboxType = params.inboxType
        if (!inboxType) {
          const inbox = state.inboxList.find(i => i.inboxId === inboxId)
          inboxType = inbox ? inbox.inboxType : ''
        }

        const finalParams = {
          ...params,
          inboxType: inboxType // 传给后端的还是 type
        }
        const response = await request({ url: 'AGENT_CHATWOOT_CONVERSATIONS_LIST', method: 'post', data: finalParams })
        const list = response.data.result || []

        // 存入缓存 (Key 为 inboxId)
        commit('SET_CONVERSATIONS_FOR_INBOX', { inboxId: inboxId, list })

      } catch (error) {
        console.error('加载会话列表失败:', error)
        throw error
      }
    },

    // [NEW] 加载所有渠道的会话 (并发加载)
    async loadAllConversations({ dispatch, state }) {
      if (!state.inboxList || state.inboxList.length === 0) return

      const promises = state.inboxList.map(inbox => {
        return dispatch('loadConversations', { inboxId: inbox.inboxId })
      })

      try {
        await Promise.all(promises)
      } catch (e) {
        console.error('加载所有会话失败', e)
      }
    },

    // [NEW] 加载客服渠道列表
    async loadInboxes({ commit }) {
      try {
        const response = await request({ url: 'AGENT_CHATWOOT_INBOX_LIST', method: 'post' })
        if (response.data.code === '000') {
          const list = response.data.result || []
          commit('SET_INBOX_LIST', list)
          // 默认选中第一个
          if (list.length > 0) {
            commit('SET_CURRENT_INBOX_ID', list[0].inboxId)
          }
        }
      } catch (error) {
        console.error('加载渠道列表失败:', error)
        throw error
      }
    },

    // [NEW] 切换渠道 (按 inboxId)
    async switchInbox({ commit, state, dispatch }, inboxId) {
      commit('SET_CURRENT_INBOX_ID', inboxId)

      // 1. 尝试从缓存加载
      // 注意：inboxId 在 Object Key 中是 string，所以 state.conversationsMap[1] 和 ['1'] 都行
      if (state.conversationsMap[inboxId] && state.conversationsMap[inboxId].length > 0) {
        commit('SET_CONVERSATIONS_FROM_CACHE', inboxId)
      } else {
        // 2. 如果没缓存，去加载
        await dispatch('loadConversations', { inboxId })
      }

      commit('SET_ACTIVE_CONVERSATION', null)
    },

    // 加载客服信息
    async loadAgentInfo({ commit }) {
      try {
        const response = await request({ url: 'AGENT_CHATWOOT_INFO', method: 'post' })
        if (response.data.code === '000') {
          commit('SET_AGENT_INFO', response.data.result)
        }
      } catch (error) {
        console.error('加载客服信息失败:', error)
        throw error
      }
    },

    // 加载消息历史
    async loadMessages({ commit, state }, { conversationId, before = null, forceRefresh = false }) {
      try {
        // 如果是初次加载且有缓存，直接使用缓存
        if (!before && !forceRefresh && state.messagesCache[conversationId]) {
          commit('SET_MESSAGES', { messages: state.messagesCache[conversationId], conversationId })
          return state.messagesCache[conversationId]
        }

        const res = await request({
          url: "AGENT_CHATWOOT_MESSAGE_LIST",
          method: "post",
          data: {
            "conversationId": conversationId,
            "before": before
          }
        })

        if (res.data.code == '000') {
          if (before) {
            commit('PREPEND_MESSAGES', res.data.result)
          } else {
            // 传递 conversationId 用于校验
            commit('SET_MESSAGES', { messages: res.data.result, conversationId })
          }
          return res.data.result
        }
      } catch (error) {
        console.error('加载消息历史失败:', error)
        throw error
      }
    },

    // 发送消息
    async sendMessage({ commit }, { conversationId, content, tempId }) {
      try {
        const data = {
          "conversationId": conversationId,
          "content": content
        }
        if (tempId) {
          data.sourceId = tempId
        }
        await request({
          url: "AGENT_CHATWOOT_MESSAGE_SEND",
          method: "post",
          data: data
        })
      } catch (error) {
        console.error('发送消息失败:', error)
        throw error
      }
    },

    // [NEW] 获取客户信息 (带缓存)
    async fetchCustomerInfo({ commit, state }, { buyerId, inboxType }) {
      if (!buyerId) return null

      // 1. 检查缓存
      // inboxType 可能是数字或字符串，统一转字符串做 key
      const cacheKey = `${inboxType}_${buyerId}`
      if (state.customerInfoCache[cacheKey]) {
        return state.customerInfoCache[cacheKey]
      }

      // 2. 缓存未命中，请求接口
      try {
        let res
        const typeStr = String(inboxType)
        switch (typeStr) {
          case '0':
            // Type 0: 会员信息
            res = await request({
              url: "TRADE_W_SHOPMEMBER_FINDBYMEMBID",
              method: "post",
              data: { id: buyerId }
            })
            break
          case '1':
            // Type 1: B2B 客户信息
            res = await request({
              url: "SZGX_W_SELLERCUSTOMERMAGE_FINDBYCUSTOMERMEMBID",
              method: "post",
              data: { id: buyerId }
            })
            break
          default:
            console.warn(`Unknown inboxType for customer info: ${typeStr}`)
            return null
        }

        if (res && res.data.code === '000') {
          const info = res.data.result || {}
          // 3. 存入缓存
          commit('SET_CUSTOMER_INFO_CACHE', { key: cacheKey, info })
          return info
        }
      } catch (error) {
        console.error('获取客户信息失败:', error)
      }
      return null
    },

    // [NEW] 获取最近订单 (带缓存)
    async fetchRecentOrders({ commit, state }, { buyerId, inboxType }) {
      if (!buyerId) return []

      // 1. 检查缓存
      const cacheKey = `${inboxType}_${buyerId}`
      if (state.recentOrdersCache[cacheKey]) {
        return state.recentOrdersCache[cacheKey]
      }

      // 2. 缓存未命中，请求接口
      try {
        let list = []
        const typeStr = String(inboxType)
        let res = null

        switch (typeStr) {
          case '0':
            // Type 0: 会员订单
            res = await request({
              url: "ORDER_FINDBYBUYERID",
              method: "post",
              data: { id: buyerId }
            })
            if (res.data.code === '000') {
              list = res.data.result || []
            }
            break
          case '1':
            // Type 1: B2B 订单
            res = await request({
              url: "SZGX_W_ORDER_FINDPAGELIST",
              method: "post",
              data: {
                buyerId: buyerId,
                pageInfo: { pageNo: 1, pageSize: 3 }, // 只取前3条
                query: '',
                orderStateType: ''
              }
            })
            if (res.data.code === '000') {
              list = res.data.result.page.list || []
            }
            break
          default:
            console.warn(`Unknown inboxType for recent orders: ${typeStr}`)
            break
        }

        // 3. 存入缓存 (即使为空也存入，避免频繁请求)
        if (res && res.data.code === '000') {
          commit('SET_RECENT_ORDERS_CACHE', { key: cacheKey, orders: list })
        }
        return list

      } catch (error) {
        console.error('获取最近订单失败:', error)
      }
      return []
    },

    // 初始化音频上下文
    initAudioContext({ commit }) {
      try {
        if (!audioContext) {
          const AudioContext = window.AudioContext || window.webkitAudioContext;
          if (AudioContext) {
            audioContext = new AudioContext();
          }
        }
        if (audioContext) {
          if (audioContext.state === 'suspended') {
            audioContext.resume().then(() => {
              commit('SET_AUDIO_UNLOCKED', true);
            }).catch(e => {
              console.warn('初始化音频上下文失败 (resume failed):', e);
            });
          } else if (audioContext.state === 'running') {
            commit('SET_AUDIO_UNLOCKED', true);
          }
        }
      } catch (e) {
        console.error('初始化音频上下文失败:', e)
      }
    },

    // 开始标题闪烁
    startTitleFlash() {
      if (titleFlashTimer) return;
      originalTitle = document.title;
      let isOriginal = true;
      titleFlashTimer = setInterval(() => {
        if (isOriginal) {
          document.title = '【新消息】' + originalTitle;
        } else {
          document.title = '【　　　】' + originalTitle;
        }
        isOriginal = !isOriginal;
      }, 500);
    },

    // 停止标题闪烁
    stopTitleFlash() {
      if (titleFlashTimer) {
        clearInterval(titleFlashTimer);
        titleFlashTimer = null;
        document.title = originalTitle;
      }
    },

    // 新消息通知
    async notifyNewMessage({ dispatch, state }, message) {
      if (state.audioUnlocked) {
        try {
          if (audioContext && audioContext.state !== 'running') {
            try { await audioContext.resume(); } catch (e) { }
          }
          if (audioContext && audioContext.state === 'running') {
            const ctx = audioContext;
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.type = 'sine';
            osc1.frequency.value = 659.25;
            gain1.gain.setValueAtTime(0.1, ctx.currentTime);
            gain1.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.4);
            osc1.start(ctx.currentTime);
            osc1.stop(ctx.currentTime + 0.4);

            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.type = 'sine';
            osc2.frequency.value = 523.25;
            gain2.gain.setValueAtTime(0.1, ctx.currentTime + 0.3);
            gain2.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 1.5);
            osc2.start(ctx.currentTime + 0.3);
            osc2.stop(ctx.currentTime + 1.5);
          }
        } catch (e) {
          console.error('播放提示音失败:', e)
        }
      }

      try {
        if (window.Notification && Notification.permission === "granted" && document.hidden) {
          new Notification("新消息", {
            body: message.content || (message.attachments?.length ? '[文件]' : '收到一条新消息')
          });
        }
      } catch (e) {
        console.error('发送浏览器通知失败:', e)
      }
    },

    // 标记会话为已读
    async markAsRead({ commit, state }, conversationId) {
      try {
        const res = await request({
          url: 'AGENT_CHATWOOT_CONVERSATION_MARK_READ',
          method: 'post',
          data: { id: parseInt(conversationId) }
        })
        if (res.data.code === '000') {
          // 调用新 Map 逻辑
          commit('MARK_CONVERSATION_READ_IN_MAP', { id: conversationId, inboxId: state.currentInboxId })
        }
      } catch (error) {
        console.error('标记已读失败:', error)
        // 乐观更新
        commit('MARK_CONVERSATION_READ_IN_MAP', { id: conversationId, inboxId: state.currentInboxId })
      }
    },

    // 更新会话 (从 socket)
    updateConversation({ commit }, conversation) {
      commit('UPDATE_CONVERSATION', conversation)
    },

    // 添加消息 (从 socket)
    addMessage({ commit }, message) {
      commit('ADD_MESSAGE', message)
    }
  },

  getters: {
    // 获取筛选后的会话列表
    filteredConversations: (state) => {
      if (state.conversationFilter === 'all') {
        return state.conversations
      }
      return state.conversations.filter(c => c.status === state.conversationFilter)
    },

    // 各个 Inbox 的未读数 { [inboxId]: 5 }
    inboxUnreadCounts: (state) => {
      const counts = {}
      for (const key in state.conversationsMap) {
        counts[key] = state.conversationsMap[key].reduce((sum, c) => sum + (c.unreadCount || 0), 0)
      }
      return counts
    },

    // 总未读数
    totalUnreadCount: (state, getters) => {
      const counts = getters.inboxUnreadCounts
      return Object.values(counts).reduce((sum, count) => sum + count, 0)
    }
  }
}

// === Helpers ===

function mapConversations(conversations) {
  return conversations.map(conv => {
    let lastMessage = conv.last_non_activity_message
    if (!lastMessage && conv.messages && conv.messages.length > 0) {
      lastMessage = conv.messages.find(msg => msg.message_type !== 2)
    }

    let lastMessageText = ''
    if (lastMessage) {
      if (lastMessage.content) {
        lastMessageText = lastMessage.content
      } else if (lastMessage.attachments && lastMessage.attachments.length > 0) {
        const attachment = lastMessage.attachments[0]
        if (attachment.file_type === 'image') {
          lastMessageText = '[图片]'
        } else if (attachment.file_type === 'audio') {
          lastMessageText = '[语音]'
        } else if (attachment.file_type === 'video') {
          lastMessageText = '[视频]'
        } else {
          lastMessageText = '[文件]'
        }
      }
    }
    return {
      id: conv.id,
      contactName: conv.meta?.sender?.name || '未知用户',
      contactAvatar: conv.meta?.sender?.thumbnail || customerDefaultAvatar,
      lastMessage: lastMessageText,
      lastMessageTime: lastMessage?.created_at
        ?formatTime(lastMessage.created_at * 1000)
        : '',
      unreadCount: conv.unread_count || 0,
      status: conv.status,
      _raw: conv,
    }
  })
}

function mapMessage(msg) {
  if (msg.messageType !== undefined) {
    return msg
  }
  return {
    id: msg.id,
    content: msg.content,
    messageType: msg.message_type,
    createdAt: msg.created_at * 1000,
    sender: {
      name: msg.sender?.name || '未知',
      type: msg.sender?.type || 'contact'
    },
    attachments: msg.attachments || [],
    status: msg.status || 'sent',
    tempId: msg.tempId,
    _raw: msg
  }
}

function updateConversationPreview(state, conversationId, messages) {
  // 简易逻辑: 如果需要更新左侧会话列表的最后一条消息显示，可以在这里实现
  // 目前已有 UPDATE_CONVERSATION 事件从 websocket 推送，这里暂时留空或做补充
}

function formatTime(ts) {
  const d = new Date(ts)
  const now = new Date()
  // 今天的消息：只显示时间
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  }
  // 昨天的消息：显示 "昨天 + 时间
  const yst = new Date(now)
  yst.setDate(yst.getDate() - 1)
  if (d.toDateString() === yst.toDateString()) {
    const time = d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    return `昨天 ${time}`
  }
  // 更早的消息：显示 "日期 + 时间"
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const time = d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  return `${month}-${day} ${time}`
}
